import { useAuth0 } from '@auth0/auth0-react';
import { useState, useEffect } from 'react';
import { doc, setDoc, getDoc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from './firebase/firebase.js';
import Sidebar from './components/Sidebar';
import { BrowserRouter as Router, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import BlogEntry from './components/BlogEntry';
import EntryDetail from './components/EntryDetail';
import translations from './translations';
import './App.css';
import GLBModel from './components/GLBModel';

const AppContent = () => {
  const { isAuthenticated, user, loginWithRedirect, logout } = useAuth0();
  const [currentLang, setCurrentLang] = useState('es');
  const [currentPage, setCurrentPage] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return parseInt(params.get('page')) || 1;
  });
  const location = useLocation();
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 736);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isParticlesEnabled, setIsParticlesEnabled] = useState(true);
  const entriesPerPage = 6;
  const totalEntries = Object.keys(translations.es).filter(key => key.startsWith('entry')).length / 2;
  const totalPages = Math.ceil(totalEntries / entriesPerPage);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 736);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isAuthenticated && user) {
      const userDoc = doc(db, 'users', user.sub);
      setLoading(true);
      getDoc(userDoc)
        .then((docSnap) => {
          if (docSnap.exists()) {
            setFavorites(docSnap.data().favorites || []);
          } else {
            setDoc(userDoc, { favorites: [] }, { merge: true });
            setFavorites([]);
          }
        })
        .catch((error) => console.error('Error fetching favorites:', error))
        .finally(() => setLoading(false));
    } else {
      setFavorites([]);
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (!isParticlesEnabled) return;

    const handleMouseMove = (e) => {
      const types = ['x', 'triangle', 'square', 'circle'];
      const type = types[Math.floor(Math.random() * types.length)];
      const particle = document.createElement('div');
      particle.className = `particle ${type}`;
      particle.innerHTML = type === 'x' ? 'x' : type === 'triangle' ? '△' : type === 'square' ? '□' : 'o';
      particle.style.left = `${e.pageX - 20}px`;
      particle.style.top = `${e.pageY - 20}px`;
      document.body.appendChild(particle);

      particle.style.animation = 'fadeIn 0.7s forwards';
      let gravity = -0.1;
      const fall = () => {
        if (particle.style.opacity !== '0') {
          gravity += 0.01;
          particle.style.top = `${parseFloat(particle.style.top) + gravity}px`;
          requestAnimationFrame(fall);
        }
      };
      requestAnimationFrame(fall);

      setTimeout(() => {
        particle.style.animation = 'fadeOut 1s forwards';
        setTimeout(() => particle.remove(), 1000);
      }, 700);
    };

    document.addEventListener('mousemove', handleMouseMove);
    return () => document.removeEventListener('mousemove', handleMouseMove);
  }, [isParticlesEnabled]);

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setIsVisible(false);
      setTimeout(() => {
        setCurrentPage(newPage);
        window.history.pushState({ page: newPage }, '', `?page=${newPage}`);
        setIsVisible(true);
      }, 500);
    }
  };

  const handleCategoryChange = (newCategory) => {
    setIsVisible(false);
    setTimeout(() => {
      setActiveCategory(newCategory);
      setCurrentPage(1);
      if (location.pathname.startsWith('/entry/')) {
        navigate(`/?page=1`);
      }
      setIsVisible(true);
    }, 500);
  };

  const toggleFavorite = async (entryId) => {
    if (!isAuthenticated) {
      alert('Por favor, inicia sesión para guardar favoritos');
      return loginWithRedirect();
    }
    const userDoc = doc(db, 'users', user.sub);
    setLoading(true);
    try {
      if (favorites.includes(entryId)) {
        await updateDoc(userDoc, { favorites: arrayRemove(entryId) });
        setFavorites(favorites.filter((id) => id !== entryId));
      } else {
        await updateDoc(userDoc, { favorites: arrayUnion(entryId) });
        setFavorites([...favorites, entryId]);
      }
    } catch (error) {
      console.error('Error updating favorites:', error);
      alert('Error al actualizar favoritos');
    } finally {
      setLoading(false);
    }
  };

  const toggleLanguage = () => {
    setCurrentLang(currentLang === 'en' ? 'es' : 'en');
  };

  const start = (currentPage - 1) * entriesPerPage + 1;
  const end = Math.min(start + entriesPerPage - 1, totalEntries);

  return (
    <div id="root" className={`no-select ${isSidebarOpen && !isMobile ? 'with-sidebar' : ''}`}>
      <Sidebar
        isOpen={isSidebarOpen}
        setIsOpen={setIsSidebarOpen}
        lang={currentLang}
        userName={isAuthenticated ? user.name : ''}
        isMobile={isMobile}
        logoutButton={
          isAuthenticated ? (
            <button
              className="logout-btn mt-2"
              onClick={() => logout({ returnTo: window.location.origin })}
            >
              <span className="lang-text">Cerrar sesión</span>
            </button>
          ) : (
            <button
              className="login-btn mt-2"
              onClick={() => loginWithRedirect()}
            >
              <span className="lang-text">Iniciar sesión</span>
            </button>
          )
        }
      />
      <div id="main" className="relative bg-[#121212] max-w-7xl mx-auto p-8 rounded-lg shadow-lg min-h-screen">
        {loading && <div className="loading-spinner absolute inset-0 flex items-center justify-center bg-[#121212]/80">Loading...</div>}
        <img
          src="/images/LogoLow.png"
          alt="Toggle Sidebar"
          className={`logo fixed top-4 left-4 w-12 h-12 cursor-pointer transition-transform ${isSidebarOpen ? 'with-sidebar' : ''}`}
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          style={{ zIndex: 1000, left: isSidebarOpen && !isMobile ? '205px' : '20px' }}
        />
        <img
          src="/images/LogoLowSimbolos.png"
          alt="Toggle Particles"
          className={`logo2 fixed top-20 left-4 w-12 h-12 cursor-pointer transition-transform ${isSidebarOpen ? 'with-sidebar' : ''}`}
          onClick={() => setIsParticlesEnabled(!isParticlesEnabled)}
          style={{ zIndex: 1000, left: isSidebarOpen && !isMobile ? '205px' : '20px' }}
        />
        <div className="language-toggle fixed top-8 right-4 z-[1000]">
          <button
            className="lang-btn bg-gradient-to-br from-[#2A233A] to-[#1C1726] border border-[#5A4C70] rounded-3xl px-4 py-2 flex items-center justify-center shadow-[0_4px_8px_rgba(0,0,0,0.3),inset_0_1px_2px_rgba(255,255,255,0.1)] hover:bg-gradient-to-br hover:from-[#3A2F4A] hover:to-[#1C1726] hover:shadow-[0_6px_12px_rgba(0,0,0,0.4),inset_0_1px_3px_rgba(255,255,255,0.2)] hover:-translate-y-0.5 transition-all"
            onClick={toggleLanguage}
          >
            <span className="lang-text text-[#8E8E8E] font-['Inter'] text-sm uppercase hover:text-white transition-colors">
              {currentLang.toUpperCase()}
            </span>
          </button>
        </div>

        {isMobile ? (
          <div className="profile-container flex flex-col items-center gap-6 max-w-full text-center">
            <div className="profile-text">
              <a href="?page=1">
                <h3
                  className="uppercase text-[#5E5E5E] font-['Inter'] tracking-[0.3rem] text-[0.5625em] leading-[2.25] font-light mb-5"
                  data-translate="devlog"
                >
                  {translations[currentLang].devlog}
                </h3>
              </a>
              <h1 className="text-[#5A4C70] font-['Manrope'] tracking-[-0.01875rem] text-[1.96875em] leading-[1.5] font-normal mb-5 inline-block">
                <span data-translate="greeting">{translations[currentLang].greeting}</span>
                <span
                  className="nickname text-[#5A4C70] font-['Manrope'] text-[0.3375em] font-normal ml-1.5 align-baseline"
                  data-translate="nickname"
                >
                  {translations[currentLang].nickname}
                </span>
              </h1>
            </div>
            <img
              src="/images/A4.png"
              alt="Perfil"
              className="profile-image w-[225px] h-[225px] rounded-full border border-black/10 p-5 object-cover hover:scale-105 transition-transform"
            />
            <div className="profile-text">
              <p
                className="text-[#A0A0A0] font-['Inter'] text-[0.75em] leading-[1.40625] font-light"
                data-translate="bio"
                dangerouslySetInnerHTML={{ __html: translations[currentLang].bio }}
              />
            </div>
          </div>
        ) : (
          <div className="profile-container flex items-start gap-12 max-w-full">
            <img
              src="/images/A4.png"
              alt="Perfil"
              className="profile-image w-[225px] h-[225px] rounded-full border border-black/10 p-5 object-cover hover:scale-105 transition-transform"
            />
            <div className="profile-text">
              <a href="?page=1">
                <h3
                  className="uppercase text-[#5E5E5E] font-['Inter'] tracking-[0.3rem] text-[0.5625em] leading-[2.25] font-light mb-5"
                  data-translate="devlog"
                >
                  {translations[currentLang].devlog}
                </h3>
              </a>
              <h1 className="text-[#5A4C70] font-['Manrope'] tracking-[-0.01875rem] text-[1.96875em] leading-[1.5] font-normal mb-5 inline-block">
                <span data-translate="greeting">{translations[currentLang].greeting}</span>
                <span
                  className="nickname text-[#5A4C70] font-['Manrope'] text-[0.3375em] font-normal ml-1.5 align-baseline"
                  data-translate="nickname"
                >
                  {translations[currentLang].nickname}
                </span>
              </h1>
              <p
                className="text-[#A0A0A0] font-['Inter'] text-[0.75em] leading-[1.40625] font-light"
                data-translate="bio"
                dangerouslySetInnerHTML={{ __html: translations[currentLang].bio }}
              />
            </div>
            <div>
              {['/images/mechito011.png', '/images/pollito.png', '/images/bcattpose.png', '/images/catitomodel.webp', '/images/razevert.png', '/images/render8.png'].map(
                (src, index) => (
                  <img
                    key={index}
                    src={src}
                    alt={`Vista previa ${index + 1}`}
                    className="preview-image absolute w-[16.66%] h-[200px] object-cover hover:scale-105 transition-transform"
                    style={{
                      left: `${55 + index * 4}%`,
                      zIndex: 6 - index,
                      clipPath: 'polygon(0 0, 30% 0, 100% 100%, 70% 100%)',
                      objectPosition: index === 0 ? '75% 0%' : 'center'
                    }}
                  />
                )
              )}
            </div>
          </div>
        )}

        <div className={`portfolio-buttons ${isMobile ? 'grid grid-cols-3 gap-2' : 'flex items-center justify-center'} my-8 mx-auto`}>
          {['all', 'modelado', 'animacion', 'arquitectura', 'motion graphics', 'videojuegos'].map((cat) => (
            <button
              key={cat}
              data-category={cat}
              className={`artstation-button uppercase font-['Inter'] tracking-[0.2rem] text-[0.65em] font-light rounded-[1.25rem] px-3 py-2 hover:bg-[#262626]/90 hover:-translate-y-0.5 transition-all ${
                activeCategory === cat
                  ? 'bg-[#5A4C70] text-white'
                  : 'bg-[#262626] text-white'
              }`}
              onClick={() => handleCategoryChange(cat)}
            >
            {cat === 'motion graphics' || cat === 'videojuegos' || cat === 'arquitectura' ? (
              <>
                {cat === 'motion graphics' ? 'motion' : cat === 'videojuegos' ? 'video' : 'arqui-'}<br/>
                {cat === 'motion graphics' ? 'graphics' : cat === 'videojuegos' ? 'juegos' : 'tectura'}
               </>
              ) : (
                cat
              )}
            </button>
          ))}
        </div>

        <Routes>
          <Route
            path="/"
            element={
              <div className={`blog-entries grid grid-cols-2 gap-6 mt-6 border border-white/10 rounded-xl p-8 shadow-[0_4px_12px_rgba(0,0,0,0.2)] bg-[#121212] ${isVisible ? 'fade-in' : 'fade-out'}`}>
                {Array.from({ length: totalEntries }, (_, i) => i + 1)
                  .filter(index => {
                    const tagsKey = `entry${index}_tags`;
                    const tags = translations[currentLang][tagsKey] || ['otros'];
                    return activeCategory === 'all' || tags.includes(activeCategory);
                  })
                  .slice((currentPage - 1) * entriesPerPage, currentPage * entriesPerPage)
                  .map(index => (
                    <div key={index} className="relative">
                      <BlogEntry index={index} lang={currentLang} />
                      <button
                        className="absolute top-2 right-2 text-[#5A4C70] hover:text-[#8E8E8E] transition-colors text-lg"
                        onClick={() => toggleFavorite(index)}
                        disabled={loading}
                      >
                        {favorites.includes(index) ? '★' : '☆'}
                      </button>
                    </div>
                  ))}
              </div>
            }
          />
          <Route
            path="/entry/:id"
            element={<EntryDetail lang={currentLang} favorites={favorites} toggleFavorite={toggleFavorite} loading={loading} glbUrl="/Fighterlow.glb" glbBotiAUrl="/3d/robotia.glb" glbClaireUrl="/3d/Claire.glb" glbRazeUrl="/3d/Raze.glb" />}
          />
        </Routes>

        {location.pathname === '/' && (
          <div className="pagination flex justify-center gap-4 mt-8">
            <a
              className={`prev-arrow flex items-center gap-2 text-[#5A4C70] font-['Inter'] font-light text-base border border-white/10 rounded px-4 py-2 hover:bg-white/5 hover:text-[#8E8E8E] hover:-translate-y-0.5 transition-all ${
                currentPage <= 1 ? 'hidden' : 'flex'
              }`}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                className="w-3 h-3 fill-[#5A4C70] hover:fill-[#8E8E8E] transition-colors"
              >
                <path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6z" />
              </svg>
              <span>Anterior</span>
            </a>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <a
                key={page}
                className={`text-[#5A4C70] font-['Inter'] font-light text-base border border-white/10 rounded px-4 py-2 hover:bg-white/5 hover:text-[#8E8E8E] hover:-translate-y-0.5 transition-all ${
                  page === currentPage ? 'bg-[#5A4C70] text-white border-[#5A4C70]' : ''
                }`}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </a>
            ))}

            <a
              className={`next-arrow flex items-center gap-2 text-[#5A4C70] font-['Inter'] font-light text-base border border-white/10 rounded px-4 py-2 hover:bg-white/5 hover:text-[#8E8E8E] hover:-translate-y-0.5 transition-all ${
                currentPage >= totalPages ? 'hidden' : 'flex'
              }`}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              <span>Siguiente</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                className="w-3 h-3 fill-[#5A4C70] hover:fill-[#8E8E8E] transition-colors"
              >
                <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z" />
              </svg>
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

const App = () => (
  <Router>
    <AppContent />
  </Router>
);

export default App;
